import React from 'react';

interface ProgressIndicatorProps {
  /**
   * Progress percentage (0-100)
   * If not provided, shows indeterminate spinner
   */
  progress?: number;
  /**
   * Size variant: 'sm' (5x5) or 'lg' (16x16)
   */
  size?: 'sm' | 'lg';
  /**
   * Whether to show on light or dark background
   */
  variant?: 'light' | 'dark';
  /**
   * Whether to show percentage text (only for 'lg' size)
   */
  showPercentage?: boolean;
}

/**
 * Unified progress indicator component used across all file views
 * Supports both determinate (upload progress) and indeterminate (import spinner) modes
 */
export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  progress,
  size = 'lg',
  variant = 'dark',
  showPercentage = false,
}) => {
  const isIndeterminate = progress === undefined;
  const isLarge = size === 'lg';
  const isLight = variant === 'light';

  // Size-specific values
  const svgSize = isLarge ? 64 : 20;
  const radius = isLarge ? 28 : 8;
  const strokeWidth = isLarge ? 4 : 1.5;
  const containerSize = isLarge ? 'w-16 h-16' : 'w-5 h-5';

  // Color classes based on variant
  const bgCircleClass = isLight
    ? 'text-gray-200 dark:text-gray-700'
    : 'text-white/30';
  const progressCircleClass = isLight
    ? 'text-gray-600 dark:text-gray-400'
    : 'text-white';
  const textClass = isLight
    ? 'text-gray-800 dark:text-gray-800'
    : 'text-white drop-shadow-lg';

  // Calculate stroke dash values
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = isIndeterminate
    ? `${circumference * 0.25} ${circumference * 0.75}` // 25% filled for spinner
    : `${circumference}`;
  const strokeDashoffset = isIndeterminate
    ? undefined
    : `${circumference * (1 - (progress || 0) / 100)}`;

  return (
    <div className={`relative ${containerSize}`}>
      <svg
        className={`${containerSize} -rotate-90`}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        style={
          isIndeterminate
            ? { animation: 'spin 1.5s linear infinite' }
            : undefined
        }
      >
        {/* Background circle */}
        <circle
          cx={svgSize / 2}
          cy={svgSize / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className={bgCircleClass}
        />
        {/* Progress/spinner circle */}
        <circle
          cx={svgSize / 2}
          cy={svgSize / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          className={progressCircleClass}
          strokeLinecap="round"
          style={
            !isIndeterminate
              ? { transition: 'stroke-dashoffset 0.3s ease' }
              : undefined
          }
        />
      </svg>
      {/* Percentage text (only for large size with progress) */}
      {isLarge && showPercentage && !isIndeterminate && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-sm font-semibold ${textClass}`}>
            {Math.round(progress || 0)}%
          </span>
        </div>
      )}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(-90deg);
          }
          to {
            transform: rotate(270deg);
          }
        }
      `}</style>
    </div>
  );
};
